/*
 * keetsa-faq-hub
 * Client-side behavior for the FAQ support hub (KEET-520):
 *   - live search filter over server-rendered Q&A (question + answer text)
 *   - single-open accordion per group
 *   - category tile smooth-scroll + focus move
 *   - result-count live region announcements
 *
 * All FAQ content is rendered server-side; search only shows/hides existing DOM.
 */
if (!customElements.get('keetsa-faq-hub')) {
  class KeetsaFaqHub extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.controller = new AbortController();
      const { signal } = this.controller;

      this.inputs = Array.from(this.querySelectorAll('[data-faq-search-input]'));
      this.clears = Array.from(this.querySelectorAll('[data-faq-search-clear]'));
      this.groups = Array.from(this.querySelectorAll('[data-faq-group]'));
      this.tiles = this.querySelector('[data-faq-tiles]');
      this.noResults = this.querySelector('[data-faq-no-results]');
      this.status = this.querySelector('[data-faq-status]');

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onInput = this.onInput.bind(this);
      this.onClear = this.onClear.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onTileClick = this.onTileClick.bind(this);

      this.inputs.forEach((input) => input.addEventListener('input', this.onInput, { signal }));
      this.clears.forEach((btn) => btn.addEventListener('click', this.onClear, { signal }));
      this.querySelectorAll('[data-faq-item]').forEach((item) =>
        item.addEventListener('toggle', this.onToggle, { signal })
      );
      this.querySelectorAll('[data-faq-tile]').forEach((tile) =>
        tile.addEventListener('click', this.onTileClick, { signal })
      );
    }

    disconnectedCallback() {
      this.controller?.abort();
      this._initialized = false;
    }

    onInput(event) {
      const value = event.target instanceof HTMLInputElement ? event.target.value : '';
      this.inputs.forEach((input) => {
        if (input !== event.target) input.value = value;
      });
      this.filter(value);
    }

    onClear() {
      this.inputs.forEach((input) => {
        input.value = '';
      });
      this.filter('');
      const first = this.inputs[0];
      if (first instanceof HTMLElement) first.focus();
    }

    filter(rawQuery) {
      const query = rawQuery.trim().toLowerCase();
      const searching = query.length > 0;

      this.clears.forEach((btn) => {
        btn.hidden = !searching;
      });
      if (this.tiles) this.tiles.hidden = searching;

      let total = 0;
      this.groups.forEach((group) => {
        let matches = 0;
        group.querySelectorAll('[data-faq-item]').forEach((item) => {
          const haystack = item.dataset.faqSearch || '';
          const match = !searching || haystack.includes(query);
          item.hidden = !match;
          if (searching && item.open) item.open = false;
          if (match) matches += 1;
        });
        group.hidden = searching && matches === 0;
        total += matches;
      });

      if (this.noResults) this.noResults.hidden = !(searching && total === 0);
      this.announce(searching, total);
    }

    announce(searching, total) {
      if (!this.status) return;
      if (!searching) {
        this.status.textContent = '';
        return;
      }
      if (total === 0) {
        this.status.textContent = this.dataset.i18nNoResults || 'No results';
        return;
      }
      const template = this.dataset.i18nResults || '%COUNT% results';
      this.status.textContent = template.replace('%COUNT%', String(total));
    }

    onToggle(event) {
      const item = event.target;
      if (!(item instanceof HTMLDetailsElement) || !item.open) return;
      const group = item.closest('[data-faq-group]');
      if (!group) return;
      group.querySelectorAll('[data-faq-item]').forEach((other) => {
        if (other !== item && other.open) other.open = false;
      });
    }

    onTileClick(event) {
      const tile = event.currentTarget;
      if (!(tile instanceof HTMLAnchorElement)) return;
      const hash = tile.getAttribute('href') || '';
      if (hash.charAt(0) !== '#' || hash.length < 2) return;

      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });

      const focusTarget = target.querySelector('[data-faq-group-heading]') || target;
      if (focusTarget instanceof HTMLElement) {
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('keetsa-faq-hub', KeetsaFaqHub);
}
